<template>
  <v-dialog v-model="internalValue" max-width="1100" persistent scrollable content-class="modern-dialog">
    <v-card class="rounded-xl overflow-hidden bg-white">
      <!-- Modal Header -->
      <v-sheet color="primary" dark class="pa-6 d-flex align-center">
        <v-avatar color="white" size="48" class="mr-4 shadow-sm">
          <v-icon color="primary">fas fa-cloud-download-alt</v-icon>
        </v-avatar>
        <div>
          <h2 class="text-h5 font-weight-black white--text lh-1">Sincronizar DTEs Recibidos</h2>
          <div class="caption white--text opacity-80 font-weight-bold uppercase tracking-widest mt-1">
            SimpleFactura &bull; Registro de Compras del SII
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closeDialog" :disabled="syncingAsync">
          <v-icon>fas fa-times</v-icon>
        </v-btn>
      </v-sheet>

      <v-card-text class="pa-6" style="max-height: 75vh;">
        <!-- Filter Controls -->
        <v-card outlined class="modern-card pa-5 mb-6 shadow-xs bg-lightgrey">
          <v-row dense align="center">
            <v-col cols="12" md="6" class="d-flex align-center">
              <v-btn-toggle v-model="filterMode" mandatory dense class="mr-3 modern-toggle">
                <v-btn small value="month" class="px-3 font-weight-bold">
                  <v-icon x-small left>fas fa-calendar-alt</v-icon> Por Mes
                </v-btn>
                <v-btn small value="range" class="px-3 font-weight-bold">
                  <v-icon x-small left>fas fa-calendar-week</v-icon> Rango Libre
                </v-btn>
              </v-btn-toggle>

              <!-- Month Navigation -->
              <div v-if="filterMode === 'month'" class="d-flex align-center bg-white rounded-lg pa-1 border-sm shadow-xs">
                <v-btn icon small @click="changeMonth(-1)" :disabled="loadingPreview || syncingAsync">
                  <v-icon x-small>fas fa-chevron-left</v-icon>
                </v-btn>
                <div class="caption font-weight-black text-uppercase px-3 primary--text text-center" style="min-width: 140px;">
                  {{ currentMonthLabel }}
                </div>
                <v-btn icon small @click="changeMonth(1)" :disabled="loadingPreview || syncingAsync">
                  <v-icon x-small>fas fa-chevron-right</v-icon>
                </v-btn>
              </div>
            </v-col>

            <!-- Custom Date Range -->
            <v-col cols="12" md="6" v-if="filterMode === 'range'" class="d-flex align-center gap-2">
              <v-text-field
                v-model="customDesde"
                type="date"
                label="Desde"
                dense outlined hide-details
                class="modern-input bg-white"
                :disabled="loadingPreview || syncingAsync"
              />
              <v-text-field
                v-model="customHasta"
                type="date"
                label="Hasta"
                dense outlined hide-details
                class="modern-input bg-white"
                :disabled="loadingPreview || syncingAsync"
              />
            </v-col>

            <!-- Consult Button & Sii Option -->
            <v-col cols="12" class="d-flex align-center justify-space-between pt-3">
              <v-checkbox
                v-model="forceConsolidate"
                label="Conciliar con SII antes de consultar"
                dense hide-details
                class="mt-0 caption font-weight-bold grey--text text--darken-2"
                :disabled="loadingPreview || syncingAsync"
              >
                <template v-slot:label>
                  <span class="caption font-weight-bold grey--text text--darken-2">
                    Conciliar con SII antes de consultar
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon x-small color="grey" v-bind="attrs" v-on="on" class="ml-1">fas fa-info-circle</v-icon>
                      </template>
                      <span>Solicita a SimpleFactura consultar el RCV del SII para asegurar que estén los últimos DTEs emitidos.</span>
                    </v-tooltip>
                  </span>
                </template>
              </v-checkbox>

              <v-btn
                color="primary"
                class="font-weight-black px-6 shadow-sm"
                :loading="loadingPreview"
                :disabled="syncingAsync"
                @click="consultarDTEs"
              >
                <v-icon left small>fas fa-search</v-icon> Consultar DTEs
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- Error Alert / Plan Requirement Banner -->
        <v-expand-transition>
          <v-alert
            v-if="errorMessage"
            outlined
            :type="isPlanError ? 'warning' : 'error'"
            prominent
            border="left"
            class="modern-card pa-4 mb-4 shadow-xs"
          >
            <div v-if="isPlanError">
              <div class="subtitle-1 font-weight-black mb-1 warning--text text--darken-3">
                <v-icon left color="warning darken-2" small>fas fa-exclamation-triangle</v-icon>
                Módulo "DTE Recibidos" no habilitado en tu cuenta SimpleFactura
              </div>
              <div class="body-2 grey--text text--darken-3">
                SimpleFactura indica que este endpoint está restringido por el plan contratado:
                <div class="my-2 pa-2 grey lighten-4 rounded font-italic caption border">
                  "{{ errorMessage }}"
                </div>
                <strong>¿Qué se necesita para habilitarlo?</strong>
                <ul class="mt-1 ml-4 caption grey--text text--darken-3">
                  <li>SimpleFactura requiere tener contratado el <strong>Plan Pyme o superior</strong>, o bien contratar el adicional <strong>"DTE Recibidos"</strong> directamente en su plataforma (<a href="https://simplefactura.cl" target="_blank" class="font-weight-bold">simplefactura.cl</a>).</li>
                  <li>Tener configurada la casilla de correo / intercambio con el SII en el portal de SimpleFactura para recibir los DTEs de tus proveedores.</li>
                </ul>
              </div>
            </div>
            <div v-else>
              <div class="subtitle-2 font-weight-bold">Error al consultar SimpleFactura</div>
              <div class="body-2 mt-1">{{ errorMessage }}</div>
            </div>
          </v-alert>
        </v-expand-transition>

        <!-- Progress Indicator for Async Task -->
        <v-expand-transition>
          <v-card v-if="syncingAsync" outlined class="modern-card pa-5 mb-6 border-primary bg-light-primary shadow-xs">
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="d-flex align-center">
                <v-progress-circular indeterminate size="18" width="2" color="primary" class="mr-3" />
                <span class="body-2 font-weight-black primary--text">{{ asyncMessage }}</span>
              </div>
              <span class="caption font-weight-black primary--text">{{ asyncProgress }}%</span>
            </div>
            <v-progress-linear
              :value="asyncProgress"
              color="primary"
              height="10"
              rounded
              striped
            ></v-progress-linear>
            <div class="caption grey--text text--darken-1 mt-2">
              El proceso se ejecuta en segundo plano. Puedes esperar o cerrar esta ventana si lo deseas.
            </div>
          </v-card>
        </v-expand-transition>

        <!-- Metrics Summary -->
        <v-row v-if="hasSearched && !loadingPreview" dense class="mb-4">
          <v-col cols="4">
            <v-card outlined class="modern-card pa-3 text-center bg-white shadow-xs border-left-primary">
              <div class="caption font-weight-bold grey--text text-uppercase tracking-wider">Total Encontrados</div>
              <div class="text-h6 font-weight-black primary--text mt-1">{{ summary.total }}</div>
            </v-card>
          </v-col>
          <v-col cols="4">
            <v-card outlined class="modern-card pa-3 text-center bg-white shadow-xs border-left-info">
              <div class="caption font-weight-bold grey--text text-uppercase tracking-wider">Nuevos Pendientes</div>
              <div class="text-h6 font-weight-black info--text mt-1">{{ summary.new_count }}</div>
            </v-card>
          </v-col>
          <v-col cols="4">
            <v-card outlined class="modern-card pa-3 text-center bg-white shadow-xs border-left-success">
              <div class="caption font-weight-bold grey--text text-uppercase tracking-wider">Ya en Runflow</div>
              <div class="text-h6 font-weight-black success--text mt-1">{{ summary.imported_count }}</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Results Table -->
        <v-card v-if="hasSearched" outlined class="modern-card overflow-hidden shadow-xs">
          <!-- Selection Controls Bar -->
          <v-sheet color="lightgrey" class="px-4 py-2 d-flex align-center border-bottom">
            <v-checkbox
              v-model="selectAllNew"
              label="Seleccionar todas las nuevas"
              dense hide-details
              class="mt-0 caption font-weight-bold"
              :disabled="syncingAsync || summary.new_count === 0"
              @change="onToggleSelectAll"
            />
            <v-spacer></v-spacer>
            <span class="caption grey--text font-weight-bold mr-3">
              Seleccionadas: {{ selectedItems.length }}
            </span>
            <v-text-field
              v-model="tableSearch"
              placeholder="Filtrar por RUT o emisor..."
              append-icon="fas fa-search"
              dense outlined hide-details single-line
              class="modern-search-field"
              style="max-width: 250px;"
            />
          </v-sheet>

          <v-data-table
            :headers="tableHeaders"
            :items="dtesList"
            :search="tableSearch"
            :loading="loadingPreview"
            dense
            class="modern-table"
            :items-per-page="15"
            :footer-props="{ itemsPerPageOptions: [10, 15, 30, 50] }"
            show-expand
            single-expand
            item-key="uid"
          >
            <!-- Checkbox column -->
            <template v-slot:item.selected="{ item }">
              <v-checkbox
                v-if="!item.already_imported"
                v-model="selectedUids[item.uid]"
                dense hide-details
                class="mt-0"
                :disabled="syncingAsync"
              />
              <v-icon v-else small color="success">fas fa-check-circle</v-icon>
            </template>

            <!-- Documento (Folio + Tipo) -->
            <template v-slot:item.documento="{ item }">
              <div class="d-flex align-center py-1">
                <span class="body-2 font-weight-black primary--text mr-2">#{{ item.folio }}</span>
                <v-chip x-small :color="getTipoColor(item.codigoSii) + ' lighten-5'" :class="getTipoColor(item.codigoSii) + '--text font-weight-black px-1'" label>
                  {{ getTipoBadge(item.codigoSii) }}
                </v-chip>
              </div>
            </template>

            <!-- Emisor -->
            <template v-slot:item.razonSocialProveedor="{ item }">
              <div class="d-flex flex-column py-1">
                <span class="body-2 font-weight-bold grey--text text--darken-3 lh-1-2 mb-0">{{ item.razonSocialProveedor }}</span>
                <span class="caption grey--text font-weight-black opacity-60">{{ item.rutProveedor }}</span>
              </div>
            </template>

            <!-- Fecha -->
            <template v-slot:item.fechaEmision="{ item }">
              <span class="caption font-weight-medium grey--text text--darken-2">{{ item.fechaEmision }}</span>
            </template>

            <!-- Total -->
            <template v-slot:item.total="{ item }">
              <span class="body-2 font-weight-black primary--text">{{ item.total | moneda }}</span>
            </template>

            <!-- Estado -->
            <template v-slot:item.already_imported="{ item }">
              <v-chip v-if="item.already_imported" x-small color="success lighten-5" class="success--text font-weight-black" label>
                <v-icon x-small left color="success">fas fa-check</v-icon>
                Importada (#{{ item.purchase_invoice_id }})
              </v-chip>
              <v-chip v-else x-small color="info lighten-5" class="info--text font-weight-black" label>
                <v-icon x-small left color="info">fas fa-clock</v-icon>
                Pendiente
              </v-chip>
            </template>

            <!-- Expanded Details Slot -->
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-4 bg-lightgrey border-top border-bottom">
                <div class="caption grey--text font-weight-black uppercase tracking-wider mb-2">
                  Detalles de Ítems / Productos ({{ item.detalles ? item.detalles.length : 0 }} líneas)
                </div>
                <v-simple-table dense class="bg-white rounded-lg border-sm">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="caption font-weight-black text-left">Código / SKU</th>
                        <th class="caption font-weight-black text-left">Nombre / Descripción</th>
                        <th class="caption font-weight-black text-right">Cantidad</th>
                        <th class="caption font-weight-black text-right">Precio Unit.</th>
                        <th class="caption font-weight-black text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(det, idx) in item.detalles" :key="idx">
                        <td class="caption grey--text">{{ det.codigo || det.sku || '-' }}</td>
                        <td class="caption font-weight-bold">{{ det.nombre || det.descripcion }}</td>
                        <td class="caption text-right">{{ det.cantidad }} {{ det.unidad || 'UN' }}</td>
                        <td class="caption text-right">{{ (det.precio || 0) | moneda }}</td>
                        <td class="caption text-right font-weight-bold">{{ (det.total || (det.precio * det.cantidad) || det.neto || 0) | moneda }}</td>
                      </tr>
                      <tr v-if="!item.detalles || item.detalles.length === 0">
                        <td colspan="5" class="caption text-center py-2 grey--text">
                          Este documento no incluyó desglose de ítems (se importará como ítem global neto ${{ item.neto | moneda }}).
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </td>
            </template>
          </v-data-table>
        </v-card>

        <!-- Empty state before search -->
        <div v-if="!hasSearched && !loadingPreview" class="pa-12 text-center">
          <v-icon size="64" color="grey lighten-2" class="mb-4">fas fa-search-dollar</v-icon>
          <div class="text-h6 font-weight-bold grey--text text--darken-1">Consulta los DTEs del Periodo</div>
          <div class="caption grey--text mt-1" style="max-width: 450px; margin: 0 auto;">
            Selecciona el mes o rango de fechas y haz clic en "Consultar DTEs" para obtener todas las facturas y notas de crédito recibidas en SimpleFactura.
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Modal Footer -->
      <v-card-actions class="pa-5 bg-lightgrey d-flex align-center justify-space-between">
        <v-btn text class="font-weight-bold px-6" @click="closeDialog" :disabled="syncingAsync">
          Cerrar
        </v-btn>

        <div class="d-flex align-center gap-3" v-if="hasSearched">
          <v-btn
            v-if="selectedCount > 0"
            color="primary"
            class="font-weight-black px-6 shadow-sm"
            :loading="syncing"
            :disabled="syncingAsync"
            @click="importSelected"
          >
            <v-icon left small>fas fa-file-import</v-icon>
            Importar Seleccionadas ({{ selectedCount }})
          </v-btn>

          <v-btn
            v-if="summary.new_count > 0"
            color="success"
            class="font-weight-black px-6 shadow-sm"
            :loading="syncing"
            :disabled="syncingAsync"
            @click="importAllNew"
          >
            <v-icon left small>fas fa-cloud-download-alt</v-icon>
            Sincronizar Todas las Nuevas ({{ summary.new_count }})
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import notifications from '@/utils/notifications'
import {
  previewSyncSimpleFactura,
  importSyncSimpleFactura,
  startAsyncSyncSimpleFactura,
  getSyncTaskStatus
} from '@/api/purchases'

export default {
  name: 'SyncSimpleFacturaDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      filterMode: 'month', // 'month' o 'range'
      currentMonthDate: moment().startOf('month'),
      customDesde: moment().startOf('month').format('YYYY-MM-DD'),
      customHasta: moment().endOf('month').format('YYYY-MM-DD'),
      forceConsolidate: false,
      loadingPreview: false,
      hasSearched: false,
      errorMessage: null,
      isPlanError: false,
      syncing: false,
      syncingAsync: false,
      asyncTaskId: null,
      asyncProgress: 0,
      asyncMessage: '',
      asyncPoller: null,
      tableSearch: '',
      selectAllNew: false,
      selectedUids: {},
      summary: {
        total: 0,
        new_count: 0,
        imported_count: 0
      },
      dtesList: [],
      tableHeaders: [
        { text: '', value: 'selected', width: '40px', sortable: false },
        { text: 'Documento', value: 'documento', width: '130px' },
        { text: 'Emisor / Proveedor', value: 'razonSocialProveedor' },
        { text: 'Fecha', value: 'fechaEmision', width: '110px' },
        { text: 'Total', value: 'total', align: 'end', width: '120px' },
        { text: 'Estado', value: 'already_imported', width: '150px' },
        { text: '', value: 'data-table-expand', width: '40px' }
      ]
    }
  },
  computed: {
    internalValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    currentMonthLabel() {
      return this.currentMonthDate.locale('es').format('MMMM YYYY')
    },
    selectedItems() {
      return this.dtesList.filter(item => !item.already_imported && this.selectedUids[item.uid])
    },
    selectedCount() {
      return this.selectedItems.length
    }
  },
  watch: {
    value(val) {
      if (val && !this.hasSearched) {
        this.consultarDTEs()
      }
    }
  },
  beforeDestroy() {
    this.stopAsyncPoller()
  },
  methods: {
    changeMonth(delta) {
      this.currentMonthDate = moment(this.currentMonthDate).add(delta, 'months').startOf('month')
      this.consultarDTEs()
    },
    closeDialog() {
      if (this.syncingAsync) return
      this.internalValue = false
    },
    getTipoColor(codigo) {
      switch (Number(codigo)) {
        case 33:
        case 30:
          return 'primary'
        case 34:
          return 'teal'
        case 52:
        case 50:
          return 'orange'
        case 56:
          return 'purple'
        case 61:
        case 60:
          return 'error'
        default:
          return 'grey'
      }
    },
    getTipoBadge(codigo) {
      switch (Number(codigo)) {
        case 33:
        case 30:
          return 'FE (33)'
        case 34:
          return 'FX (34)'
        case 52:
        case 50:
          return 'GD (52)'
        case 56:
          return 'ND (56)'
        case 61:
        case 60:
          return 'NC (61)'
        default:
          return `DTE ${codigo}`
      }
    },
    onToggleSelectAll(val) {
      const newUids = {}
      this.dtesList.forEach(item => {
        if (!item.already_imported) {
          newUids[item.uid] = val
        }
      })
      this.selectedUids = newUids
    },
    getDateRange() {
      if (this.filterMode === 'month') {
        const start = moment(this.currentMonthDate).startOf('month').format('YYYY-MM-DD')
        const end = moment(this.currentMonthDate).endOf('month').format('YYYY-MM-DD')
        return { desde: start, hasta: end }
      }
      return { desde: this.customDesde, hasta: this.customHasta }
    },
    async consultarDTEs() {
      const { desde, hasta } = this.getDateRange()
      if (!desde || !hasta) {
        notifications.showError('Selecciona un rango de fechas válido')
        return
      }

      this.loadingPreview = true
      this.selectedUids = {}
      this.selectAllNew = false
      this.errorMessage = null
      this.isPlanError = false

      try {
        const res = await previewSyncSimpleFactura({
          desde,
          hasta,
          force_consolidate: this.forceConsolidate
        })

        if (res && res.status === 'ok') {
          const data = res.data
          this.summary = {
            total: data.total,
            new_count: data.new_count,
            imported_count: data.imported_count
          }
          this.dtesList = data.results || []
          this.hasSearched = true

          // Preseleccionar todas las nuevas por defecto si son <= 20
          if (this.summary.new_count > 0 && this.summary.new_count <= 20) {
            this.selectAllNew = true
            this.onToggleSelectAll(true)
          }
        }
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message
        this.errorMessage = msg
        if (msg && (msg.toLowerCase().includes('plan pyme') || msg.toLowerCase().includes('dte recibidos') || msg.toLowerCase().includes('requiere plan'))) {
          this.isPlanError = true
        }
        notifications.showError('Error consultando SimpleFactura: ' + msg)
      } finally {
        this.loadingPreview = false
      }
    },
    async importSelected() {
      const itemsToImport = this.selectedItems
      if (itemsToImport.length === 0) return
      await this.executeImport(itemsToImport)
    },
    async importAllNew() {
      const allNew = this.dtesList.filter(item => !item.already_imported)
      if (allNew.length === 0) {
        notifications.showInfo('No hay documentos nuevos pendientes de importar')
        return
      }
      await this.executeImport(allNew)
    },
    async executeImport(items) {
      // Si son más de 10 documentos, usamos asincronía ligera para máxima suavidad
      if (items.length > 10) {
        await this.executeImportAsync(items)
      } else {
        await this.executeImportSync(items)
      }
    },
    async executeImportSync(items) {
      this.syncing = true
      try {
        const res = await importSyncSimpleFactura({ dtes: items })
        if (res && res.status === 'ok') {
          notifications.showSuccess(res.message || 'Facturas importadas correctamente')
          this.$emit('synced')
          await this.consultarDTEs()
        }
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message
        notifications.showError('Error importando documentos: ' + msg)
      } finally {
        this.syncing = false
      }
    },
    async executeImportAsync(items) {
      this.syncing = true
      try {
        const res = await startAsyncSyncSimpleFactura({ dtes: items })
        if (res && res.task_id) {
          this.asyncTaskId = res.task_id
          this.syncingAsync = true
          this.asyncProgress = 0
          this.asyncMessage = 'Iniciando sincronización en segundo plano...'
          this.startAsyncPoller()
        }
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message
        notifications.showError('Error iniciando sincronización: ' + msg)
        this.syncing = false
      }
    },
    startAsyncPoller() {
      this.stopAsyncPoller()
      this.asyncPoller = setInterval(async () => {
        if (!this.asyncTaskId) return
        try {
          const res = await getSyncTaskStatus(this.asyncTaskId)
          if (res && res.task) {
            const task = res.task
            this.asyncProgress = task.progress || 0
            this.asyncMessage = task.message || 'Procesando...'

            if (task.status === 'completed') {
              this.stopAsyncPoller()
              this.syncingAsync = false
              this.syncing = false
              notifications.showSuccess(task.message || 'Sincronización completada con éxito')
              this.$emit('synced')
              await this.consultarDTEs()
            } else if (task.status === 'error') {
              this.stopAsyncPoller()
              this.syncingAsync = false
              this.syncing = false
              notifications.showError('Error en la tarea: ' + task.message)
            }
          }
        } catch (err) {
          console.error('Error consultando status de sincronización:', err)
        }
      }, 1500)
    },
    stopAsyncPoller() {
      if (this.asyncPoller) {
        clearInterval(this.asyncPoller)
        this.asyncPoller = null
      }
    }
  }
}
</script>

<style scoped>
.modern-toggle {
  background: #ffffff !important;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}
.border-left-primary {
  border-left: 4px solid #1976d2 !important;
}
.border-left-info {
  border-left: 4px solid #0288d1 !important;
}
.border-left-success {
  border-left: 4px solid #43a047 !important;
}
.border-sm {
  border: 1px solid #e0e0e0;
}
.bg-lightgrey {
  background-color: #f8fafc;
}
.bg-light-primary {
  background-color: #f0f7ff;
}
</style>
